import React from "react";
import {
  Box,
  Button,
  ButtonDropdown,
  FormField,
  Link,
  RadioGroup,
  SpaceBetween,
  StatusIndicator,
} from "@amzn/awsui-components-react";
import {
  Investigation,
  InvestigationBlockerCodeReview,
  InvestigationUpdateRequest,
} from "../../lusca-search-api/generated-src";
import { REDRIVE_OPTIONS } from "./RedriveOptionsModal";
import { STATUS_LABELS, formatTimestamp } from "./investigationUtils";

/**
 * Blocking a case on a CR (LuscaSearchDevDocs specs/dlq-investigations/cr-blocker): Lusca follows
 * the CR from merge to the case's stage, then unblocks the case or launches the queued redrive.
 */
export type AfterDeploy = "NONE" | "DOCUMENT_REDRIVE" | "DLQ_REDRIVE";
export const AFTER_DEPLOY_VALUES: AfterDeploy[] = [
  "NONE",
  "DOCUMENT_REDRIVE",
  "DLQ_REDRIVE",
];
export const AFTER_DEPLOY_LABELS: Record<AfterDeploy, string> = {
  NONE: "Just unblock",
  DOCUMENT_REDRIVE: "Run the order redrive",
  DLQ_REDRIVE: "Run the DLQ redrive",
};
/** The states in which Lusca is still following the CR. */
const LIVE_STATES = ["CHECKING", "WAITING_FOR_MERGE", "MERGED"];

const CR_ID = /^CR-(\d{1,12})$/i;
const CR_URL =
  /^https?:\/\/(?:code\.amazon\.com|cr\.amazon\.dev)\/reviews\/CR-(\d{1,12})(?:[/?#].*)?$/i;

/** Same rule as the backend: `CR-123` or an approved review link → `CR-123`. */
export function parseCodeReviewId(text: string): string | null {
  const value = text.trim();
  const match = CR_ID.exec(value) || CR_URL.exec(value);
  return match ? `CR-${match[1]}` : null;
}

export const codeReviewUrl = (id: string) =>
  `https://code.amazon.com/reviews/${id}`;

export const defaultBlockerReason = (id: string, stage: string) =>
  `Waiting for ${id} to reach ${stage}`;

/** The blocker link to send: a bare `CR-123` becomes its review URL (the API only takes http(s)). */
export function normalizeBlockerLink(text: string): string | undefined {
  const value = text.trim();
  if (!value) return undefined;
  const id = CR_ID.test(value) ? parseCodeReviewId(value) : null;
  return id ? codeReviewUrl(id) : value;
}

/** Description of the blocker link field where Lusca follows CRs: a CR link starts the wait. */
export const codeReviewLinkHint = (stage: string) =>
  `Link a CR (CR-123 or a code.amazon.com/cr.amazon.dev link) and Lusca follows it to ${stage}.`;

/** The CR a BLOCKED case is still waiting on (a cleared blocker is history). */
export function waitingOnCodeReview(
  investigation: Pick<Investigation, "status" | "blocker">
): InvestigationBlockerCodeReview | undefined {
  const { blocker } = investigation;
  const codeReview = blocker?.codeReview;
  if (investigation.status !== "BLOCKED" || blocker?.clearedAt || !codeReview)
    return undefined;
  return LIVE_STATES.includes(codeReview.state || "CHECKING")
    ? codeReview
    : undefined;
}

/** Why a follow-up can't be queued now; the API repeats these checks and the redrive lock. */
export function afterDeployDisabledReason(
  investigation: Investigation,
  value: AfterDeploy
): string | undefined {
  if (value === "NONE") return undefined;
  if (investigation.redriveMode === "external")
    return `${investigation.stage} redrives are run in Lusca Search Prod.`;
  if (investigation.pendingDeletion)
    return "A delete-without-redrive request is pending.";
  if (!investigation.assignedCount)
    return "The investigation owns no DLQ items.";
  // Same test as the API (document_redrive_unconfirmed): the marker survives a move to Blocked,
  // so don't use awaitingConfirmation(), which also requires READY_TO_REDRIVE.
  if (
    value === "DLQ_REDRIVE" &&
    investigation.documentRedrive?.stage === "AWAITING_CONFIRMATION"
  )
    return "Check the order redrive's CBEs first.";
  return undefined;
}

function afterDeployDescription(value: AfterDeploy, backTo: string): string {
  if (value === "NONE")
    return `Moves the case back to ${backTo}; you take it from there.`;
  const option = REDRIVE_OPTIONS.find(
    (o) => o.id === (value === "DLQ_REDRIVE" ? "dlq" : "document")
  );
  const what = option ? option.tagline : "Starts the redrive";
  return `${what}. Lusca starts it as you as soon as the CR is deployed.`;
}

interface CodeReviewFollowUpProps {
  investigation: Investigation;
  /** The CR in the blocker link. */
  codeReviewId: string;
  afterDeploy: AfterDeploy;
  onAfterDeployChange: (value: AfterDeploy) => void;
  /** Status label the case returns to with "Just unblock". */
  backTo: string;
}

/**
 * "When CR-X reaches <stage>" in the "Move to Blocked" form, shown once the blocker link is a CR:
 * Lusca follows the CR from merge to the case's stage, then does the chosen follow-up.
 */
export const CodeReviewFollowUp: React.FC<CodeReviewFollowUpProps> = ({
  investigation,
  codeReviewId,
  afterDeploy,
  onAfterDeployChange,
  backTo,
}) => (
  <FormField label={`When ${codeReviewId} reaches ${investigation.stage}`}>
    <RadioGroup
      value={afterDeploy}
      onChange={({ detail }) =>
        onAfterDeployChange(detail.value as AfterDeploy)
      }
      items={AFTER_DEPLOY_VALUES.map((option) => {
        const reason = afterDeployDisabledReason(investigation, option);
        return {
          value: option,
          label: AFTER_DEPLOY_LABELS[option],
          description: reason || afterDeployDescription(option, backTo),
          disabled: !!reason,
        };
      })}
    />
  </FormField>
);

function stateLine(
  codeReview: InvestigationBlockerCodeReview,
  stage: string
): React.ReactNode {
  const { id } = codeReview;
  const packages = codeReview.packages || [];
  const deployed = packages.filter((p) => p.deployedAt).length;
  switch (codeReview.state) {
    case "WAITING_FOR_MERGE":
      return (
        <StatusIndicator type="pending">
          Waiting for {id} to merge
          {codeReview.source === "COMMIT_MESSAGES"
            ? ". Lusca finds its commits in new PVAccounting builds by the CR link in their message, so it can't tell if the CR is discarded: stop waiting by hand if it is."
            : ""}
        </StatusIndicator>
      );
    case "MERGED":
      return (
        <StatusIndicator type="in-progress">
          Merged
          {codeReview.mergedAt
            ? ` ${formatTimestamp(codeReview.mergedAt)}`
            : ""}
          ; waiting for {stage}
          {packages.length > 1
            ? ` (${deployed} of ${packages.length} packages deployed)`
            : ""}
        </StatusIndicator>
      );
    case "DEPLOYED":
      return (
        <StatusIndicator type="success">
          Deployed to {stage}
          {codeReview.deployedRevision
            ? ` in ${codeReview.deployedRevision}`
            : ""}
        </StatusIndicator>
      );
    case "DISCARDED":
      return (
        <StatusIndicator type="error">
          {id} was discarded. Attach another CR or unblock by hand.
        </StatusIndicator>
      );
    case "INVALID":
      return (
        <StatusIndicator type="error">
          Lusca can&apos;t follow {id}: {codeReview.stateReason}
        </StatusIndicator>
      );
    default:
      return <StatusIndicator type="loading">Checking {id}…</StatusIndicator>;
  }
}

interface CodeReviewWaitProps {
  investigation: Investigation;
  isOwner: boolean;
  loading?: boolean;
  /** Saves a blocker change (the caller adds expectedVersion). */
  onUpdate: (
    request: Omit<InvestigationUpdateRequest, "expectedVersion">
  ) => void;
}

/** "Waiting for CR-X" inside the Blocked alert: progress, the queued follow-up, Change/Stop. */
export const CodeReviewWait: React.FC<CodeReviewWaitProps> = ({
  investigation,
  isOwner,
  loading,
  onUpdate,
}) => {
  const { blocker, stage } = investigation;
  const codeReview = blocker?.codeReview;
  if (!blocker || !codeReview) return null;
  const live = !!waitingOnCodeReview(investigation);
  const afterDeploy = (codeReview.afterDeploy || "NONE") as AfterDeploy;
  const packages = codeReview.packages || [];
  const editable = {
    reason: blocker.reason,
    link: blocker.link,
    nextReviewDate: blocker.nextReviewDate,
  };
  const change = (next: AfterDeploy) =>
    onUpdate({
      blocker: {
        ...editable,
        codeReview: { id: codeReview.id, afterDeploy: next },
      },
    });
  const stop = () => onUpdate({ blocker: editable });
  const queuedByOther =
    afterDeploy !== "NONE" &&
    !!codeReview.queuedBy &&
    codeReview.queuedBy !== investigation.owner;
  const backTo = blocker.blockedFrom
    ? STATUS_LABELS[blocker.blockedFrom] || blocker.blockedFrom
    : STATUS_LABELS.INVESTIGATING;

  return (
    <SpaceBetween size="xxs" data-testid="code-review-wait">
      <Box>
        <Box variant="strong" display="inline">
          Waiting for{" "}
          <Link href={codeReviewUrl(codeReview.id)} external>
            {codeReview.id}
          </Link>{" "}
          to reach {stage}
        </Box>
      </Box>
      {stateLine(codeReview, stage)}
      {packages.length > 1 && (
        <Box fontSize="body-s" color="text-body-secondary">
          {packages
            .map(
              (p) =>
                `${p.name}: ${
                  p.deployedAt
                    ? `deployed ${formatTimestamp(p.deployedAt)}`
                    : "waiting"
                }`
            )
            .join(" · ")}
        </Box>
      )}
      {!!codeReview.ignoredPackages?.length && (
        <Box fontSize="body-s" color="text-body-secondary">
          Not built by the PVAccounting pipeline, so not followed:{" "}
          {codeReview.ignoredPackages.join(", ")}
        </Box>
      )}
      {live && codeReview.lastError && (
        <Box fontSize="body-s" color="text-status-warning">
          The last check failed ({codeReview.lastError}); Lusca retries hourly.
        </Box>
      )}
      <Box fontSize="body-s" data-testid="code-review-follow-up">
        When it is deployed: {AFTER_DEPLOY_LABELS[afterDeploy]}
        {afterDeploy === "NONE" ? ` (back to ${backTo})` : ""}
        {afterDeploy !== "NONE" && codeReview.queuedBy
          ? `, queued by ${codeReview.queuedBy}`
          : ""}
        {queuedByOther &&
          `. It runs as ${codeReview.queuedBy} unless you change it.`}
      </Box>
      {isOwner && live && (
        <SpaceBetween direction="horizontal" size="xs">
          <ButtonDropdown
            variant="normal"
            loading={loading}
            items={AFTER_DEPLOY_VALUES.map((value) => {
              const reason = afterDeployDisabledReason(investigation, value);
              return {
                id: value,
                text: AFTER_DEPLOY_LABELS[value],
                disabled: !!reason || (value === afterDeploy && !queuedByOther),
                disabledReason: reason,
              };
            })}
            onItemClick={({ detail }) => change(detail.id as AfterDeploy)}
          >
            Change what happens
          </ButtonDropdown>
          <Button variant="link" onClick={stop} loading={loading}>
            Stop waiting for {codeReview.id}
          </Button>
        </SpaceBetween>
      )}
    </SpaceBetween>
  );
};
